/* eslint-disable no-restricted-properties */
import React from 'react';
import type { ColumnsType } from 'antd/es/table';
import { Tooltip } from 'antd';
import { TitleHeader, TitleHeaderArea } from './style';
import { Planogram, Store, Product } from '../../store/Planogram/findAll';
import { convertCurrency } from '../../helpers/convertCurrency';
import { CellWithTooltip } from '../../components/DataView/EditableTable';
import { PlanogramUI } from './context/PlanogramAutomation';

enum Status {
    unlocked = 'Desbloqueado',
    lock = 'Bloqueado para abastecimento',
    lockSold = 'Bloqueado para venda',
}

export interface DataTypeSystem {
    key: string;
    giro: number;
    price: string;
}

export interface DataTypeExpectedMetrics {
    key: string;
    stock_days: number;
    margin: number;
}

export const overlayInnerStyle: React.CSSProperties = {
    backgroundColor: '#fff',
    color: '#000',
    borderRadius: '11px',
    textAlign: 'center',
};

const truncatetNumber = (number: number, digits: number) => {
    return Math.trunc(number * Math.pow(10, digits)) / Math.pow(10, digits);
};

export const planogramColumn: ColumnsType<PlanogramUI> = [
    {
        title: (
            <TitleHeaderArea>
                <TitleHeader>Loja</TitleHeader>
            </TitleHeaderArea>
        ),
        dataIndex: 'store',
        key: 'store',
        width: 148,
        align: 'center',
        ellipsis: true,
        render: (store: Store) => (
            <Tooltip title={store?.name}>
                <div
                    style={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                    }}
                >
                    {store?.name}
                </div>
            </Tooltip>
        ),
    },
    {
        title: (
            <TitleHeaderArea>
                <TitleHeader>Nome do produto</TitleHeader>
            </TitleHeaderArea>
        ),
        width: 170,
        render: (planogram: Planogram) => (
            <Tooltip title={planogram.product.name}>
                <div
                    style={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                    }}
                >
                    {planogram.product.name}
                </div>
            </Tooltip>
        ),
    },
    {
        title: (
            <TitleHeaderArea>
                <TitleHeader>Código de Barras</TitleHeader>
            </TitleHeaderArea>
        ),
        dataIndex: 'product',
        key: 'product',
        width: 170,
        align: 'center',
        ellipsis: true,
        render: (product: Product) =>
            product.BarCodes.length > 0
                ? product.BarCodes[0].code
                : 'Sem código de barras',
    },
    {
        title: (
            <TitleHeaderArea>
                <TitleHeader>Status</TitleHeader>
            </TitleHeaderArea>
        ),
        dataIndex: 'status',
        key: 'status',
        width: 148,
        align: 'center',
        ellipsis: true,
        render: (status: keyof typeof Status) => Status[status],
    },
    {
        title: (
            <Tooltip
                color="#fff"
                overlayInnerStyle={overlayInnerStyle}
                title="Quantidade de produtos para a loja 100% abastecida"
            >
                <TitleHeaderArea>
                    <TitleHeader>Quantidade Padrão</TitleHeader>
                </TitleHeaderArea>
            </Tooltip>
        ),
        dataIndex: 'standard_amount',
        key: 'standard_amount',
        width: 148,
        align: 'center',
        ellipsis: true,
    },
    {
        title: (
            <Tooltip
                color="#fff"
                overlayInnerStyle={overlayInnerStyle}
                title="Preço unitário padrão do produto (não mostrado para o usuário)"
            >
                <TitleHeaderArea>
                    <TitleHeader>Preço lista un.</TitleHeader>
                </TitleHeaderArea>
            </Tooltip>
        ),
        key: 'price_list_in_cents',
        width: 148,
        align: 'center',
        ellipsis: true,
        render: (_, order) => (
            <CellWithTooltip
                valueCell={convertCurrency(
                    '',
                    order?.price_list_in_cents_after ??
                        order.price_list_in_cents,
                )}
                ptbrCurrency
            />
        ),
    },
    {
        title: (
            <Tooltip
                color="#fff"
                overlayInnerStyle={overlayInnerStyle}
                title="Preço unitário no aplicativo (aparece para o usuário)"
            >
                <TitleHeaderArea>
                    <TitleHeader>Preço App un.</TitleHeader>
                </TitleHeaderArea>
            </Tooltip>
        ),
        key: 'price_final_in_cents',
        width: 148,
        align: 'center',
        ellipsis: true,
        render: (_, order) => (
            <CellWithTooltip
                valueCell={convertCurrency(
                    '',
                    order?.price_final_in_cents_after ??
                        order.price_final_in_cents,
                )}
                ptbrCurrency
            />
        ),
    },
];

export const systemColumn: ColumnsType<PlanogramUI> = [
    {
        title: (
            <Tooltip
                color="#fff"
                overlayInnerStyle={overlayInnerStyle}
                title="Quantidade vendida nos últimos 7 dias ou, se não houver, 7 dias de vendas médias no mês"
            >
                <TitleHeaderArea>
                    <TitleHeader>Giro atual (7 dias)</TitleHeader>
                </TitleHeaderArea>
            </Tooltip>
        ),
        dataIndex: 'stock_turnover_seven_days',
        key: 'stock_turnover_seven_days',
        width: 148,
        align: 'center',
        render: (stock_turnover_serven_days: number) => {
            return stock_turnover_serven_days % 1 !== 0
                ? truncatetNumber(stock_turnover_serven_days, 1)
                : stock_turnover_serven_days;
        },
    },
    {
        title: (
            <Tooltip
                color="#fff"
                overlayInnerStyle={overlayInnerStyle}
                title="Último preço de compra unitário do produto"
            >
                <TitleHeaderArea>
                    <TitleHeader>Preço de compra un.</TitleHeader>
                </TitleHeaderArea>
            </Tooltip>
        ),
        dataIndex: 'product',
        key: 'product',
        width: 148,
        align: 'center',
        render: (product: Product) => (
            <CellWithTooltip
                valueCell={convertCurrency('', product.last_price_in_cents)}
                ptbrCurrency
            />
        ),
    },
];

export const expectedMetricsColumn: ColumnsType<PlanogramUI> = [
    {
        title: (
            <Tooltip
                color="#fff"
                overlayInnerStyle={overlayInnerStyle}
                title="Quantos dias o estoque deve atender a demanda - definido pelo lojista"
            >
                <TitleHeaderArea>
                    <TitleHeader>Dias de Estoque (esperado)</TitleHeader>
                </TitleHeaderArea>
            </Tooltip>
        ),
        width: 156,
        align: 'center',
        render: (planogram: Planogram) => planogram.expected_store_stock || '-',
    },
    {
        title: (
            <Tooltip
                color="#fff"
                overlayInnerStyle={overlayInnerStyle}
                title="Margem de lucro bruta esperada para o produto"
            >
                <TitleHeaderArea>
                    <TitleHeader>Margem Bruta (esperada)</TitleHeader>
                </TitleHeaderArea>
            </Tooltip>
        ),
        width: 148,
        align: 'center',
        render: (planogram: Planogram) => {
            let gross_margin = '';

            if (planogram.expected_gross_margin) {
                gross_margin = `${(
                    planogram.expected_gross_margin * 100
                ).toFixed()}%`;
            }

            return gross_margin !== '' ? gross_margin : '-';
        },
    },
];

export const currentMetricsColumns: ColumnsType<PlanogramUI> = [
    {
        title: (
            <Tooltip
                color="#fff"
                overlayInnerStyle={overlayInnerStyle}
                title="Quantos dias o estoque atual consegue atender a demanda atual"
            >
                <TitleHeaderArea>
                    <TitleHeader>Dias de Estoque (atual)</TitleHeader>
                </TitleHeaderArea>
            </Tooltip>
        ),
        dataIndex: 'actual_store_stock',
        key: 'actual_store_stock',
        width: 167,
        align: 'center',
        render: (actual_store_stock) =>
            actual_store_stock !== Infinity && actual_store_stock !== null
                ? Math.round(actual_store_stock)
                : '-',
    },
    {
        title: (
            <Tooltip
                color="#fff"
                overlayInnerStyle={overlayInnerStyle}
                title="Margem de lucro bruta com base no preço atual do aplicativo"
            >
                <TitleHeaderArea>
                    <TitleHeader>Margem Bruta (preço app. atual)</TitleHeader>
                </TitleHeaderArea>
            </Tooltip>
        ),
        dataIndex: 'actual_gross_margin',
        key: 'actual_gross_margin',
        width: 167,
        align: 'center',
        render: (actual_gross_margin: number) => {
            const stock_to_percentage = actual_gross_margin
                ? `${Math.round(actual_gross_margin * 100)}%`
                : '-';

            return `${stock_to_percentage}`;
        },
    },
];
